import { InspectorControls } from "@wordpress/block-editor";
import {
    PanelBody,
    ToggleControl,
    __experimentalView as View,
} from "@wordpress/components";
import React from "react";

import ResponsiveRange from "./components/ResponsiveRange";

const { addFilter } = wp.hooks;
const { subscribe } = wp.data;

// Dodaj listę obsługiwanych bloków
const SUPPORTED_BLOCKS = [
    "core/button",
    "core/heading",
    "core/group",
    "core/columns",
    "core/cover",
    "core/paragraph",
    "core/search",
    "core/list",
    "core/list-item",
    "kamikaze/custom-icon",
];

// Rozszerzenie atrybutów bloku
const spacingExtension = (settings, name) => {
    if (!SUPPORTED_BLOCKS.includes(name)) {
        return settings;
    }

    // Zachowaj istniejące atrybuty
    const existingAttributes = settings.attributes || {};

    return {
        ...settings,
        attributes: {
            ...existingAttributes,
            enableCustomSpacing: {
                type: "boolean",
                default: false,
            },
            marginDesktop: {
                type: "object",
                default: {
                    top: null,
                    right: null,
                    bottom: null,
                    left: null,
                    blockStart: null,
                    blockEnd: null,
                    topUnit: "px",
                    rightUnit: "px",
                    bottomUnit: "px",
                    leftUnit: "px",
                    blockStartUnit: "px",
                    blockEndUnit: "px",
                },
            },
            marginTablet: {
                type: "object",
                default: {
                    top: null,
                    right: null,
                    bottom: null,
                    left: null,
                    blockStart: null,
                    blockEnd: null,
                    topUnit: "px",
                    rightUnit: "px",
                    bottomUnit: "px",
                    leftUnit: "px",
                    blockStartUnit: "px",
                    blockEndUnit: "px",
                },
            },
            marginMobile: {
                type: "object",
                default: {
                    top: null,
                    right: null,
                    bottom: null,
                    left: null,
                    blockStart: null,
                    blockEnd: null,
                    topUnit: "px",
                    rightUnit: "px",
                    bottomUnit: "px",
                    leftUnit: "px",
                    blockStartUnit: "px",
                    blockEndUnit: "px",
                },
            },
            paddingDesktop: {
                type: "object",
                default: {
                    top: null,
                    right: null,
                    bottom: null,
                    left: null,
                    topUnit: "px",
                    rightUnit: "px",
                    bottomUnit: "px",
                    leftUnit: "px",
                },
            },
            paddingTablet: {
                type: "object",
                default: {
                    top: null,
                    right: null,
                    bottom: null,
                    left: null,
                    topUnit: "px",
                    rightUnit: "px",
                    bottomUnit: "px",
                    leftUnit: "px",
                },
            },
            paddingMobile: {
                type: "object",
                default: {
                    top: null,
                    right: null,
                    bottom: null,
                    left: null,
                    topUnit: "px",
                    rightUnit: "px",
                    bottomUnit: "px",
                    leftUnit: "px",
                },
            },
        },
    };
};

const withSpacingControls = wp.compose.createHigherOrderComponent(
    (BlockEdit) => {
        return (props) => {
            if (!SUPPORTED_BLOCKS.includes(props.name)) {
                return <BlockEdit {...props} />;
            }

            const { attributes, setAttributes, clientId } = props;

            const updateSpacing = (
                type,
                device,
                direction,
                value,
                unit = "px"
            ) => {
                const currentSpacingKey = `${type}${
                    device.charAt(0).toUpperCase() + device.slice(1)
                }`;
                const currentSpacing = attributes[currentSpacingKey] || {};

                const newSpacing = {
                    ...currentSpacing,
                    [direction]: value,
                    [`${direction}Unit`]: unit,
                };

                // Usuń zagnieżdżone obiekty jednostek, jeśli przypadkiem się pojawią
                Object.keys(newSpacing).forEach((key) => {
                    if (
                        newSpacing[key] &&
                        typeof newSpacing[key] === "object" &&
                        "unit" in newSpacing[key]
                    ) {
                        newSpacing[key] = newSpacing[key].value;
                    }
                });

                setAttributes({
                    [currentSpacingKey]: newSpacing,
                });
            };

            const resetSpacing = (type, device) => {
                const currentSpacingKey = `${type}${
                    device.charAt(0).toUpperCase() + device.slice(1)
                }`;

                const resetValues = {
                    [currentSpacingKey]: {
                        top: null,
                        right: null,
                        bottom: null,
                        left: null,
                        topUnit: "px",
                        rightUnit: "px",
                        bottomUnit: "px",
                        leftUnit: "px",
                        ...(type === "margin"
                            ? {
                                  blockStart: null,
                                  blockEnd: null,
                                  blockStartUnit: "px",
                                  blockEndUnit: "px",
                              }
                            : {}),
                    },
                };

                setAttributes(resetValues);
                xw;
            };

            const generateSpacingValue = (spacingObj) => {
                if (!spacingObj) return null;
                const { top, right, bottom, left, blockStart, blockEnd } =
                    spacingObj;
                const topUnit = spacingObj.topUnit || "px";
                const rightUnit = spacingObj.rightUnit || "px";
                const bottomUnit = spacingObj.bottomUnit || "px";
                const leftUnit = spacingObj.leftUnit || "px";
                const blockStartUnit = spacingObj.blockStartUnit || "px";
                const blockEndUnit = spacingObj.blockEndUnit || "px";

                return {
                    top: top !== null ? `${top}${topUnit}` : null,
                    right: right !== null ? `${right}${rightUnit}` : null,
                    bottom: bottom !== null ? `${bottom}${bottomUnit}` : null,
                    left: left !== null ? `${left}${leftUnit}` : null,
                    blockStart:
                        blockStart !== null
                            ? `${blockStart}${blockStartUnit}`
                            : null,
                    blockEnd:
                        blockEnd !== null ? `${blockEnd}${blockEndUnit}` : null,
                };
            };

            const editorSpacingStyles = `
                #block-${clientId}.has-custom-spacing{
                    box-sizing: border-box !important;
                    /* Desktop styles */
                    ${(() => {
                        const marginDesktop = generateSpacingValue(
                            attributes.marginDesktop
                        );
                        const paddingDesktop = generateSpacingValue(
                            attributes.paddingDesktop
                        );
                        return `
                            ${
                                marginDesktop?.top
                                    ? `margin-top: ${marginDesktop.top}!important;`
                                    : ""
                            }
                            ${
                                marginDesktop?.right
                                    ? `margin-right: ${marginDesktop.right}!important;`
                                    : ""
                            }
                            ${
                                marginDesktop?.bottom
                                    ? `margin-bottom: ${marginDesktop.bottom}!important;`
                                    : ""
                            }
                            ${
                                marginDesktop?.left
                                    ? `margin-left: ${marginDesktop.left}!important;`
                                    : ""
                            }
                            ${
                                marginDesktop?.blockStart
                                    ? `margin-block-start: ${marginDesktop.blockStart}!important;`
                                    : ""
                            }
                            ${
                                marginDesktop?.blockEnd
                                    ? `margin-block-end: ${marginDesktop.blockEnd}!important;`
                                    : ""
                            }
                            ${
                                paddingDesktop?.top
                                    ? `padding-top: ${paddingDesktop.top}!important;`
                                    : ""
                            }
                            ${
                                paddingDesktop?.right
                                    ? `padding-right: ${paddingDesktop.right}!important;`
                                    : ""
                            }
                            ${
                                paddingDesktop?.bottom
                                    ? `padding-bottom: ${paddingDesktop.bottom}!important;`
                                    : ""
                            }
                            ${
                                paddingDesktop?.left
                                    ? `padding-left: ${paddingDesktop.left}!important;`
                                    : ""
                            }
                        `;
                    })()}
                }

                @media (max-width: 1024px) and (min-width: 768px) {
                    #block-${clientId}.has-custom-spacing{
                        /* Tablet styles */
                        ${(() => {
                            const marginTablet = generateSpacingValue(
                                attributes.marginTablet
                            );
                            const paddingTablet = generateSpacingValue(
                                attributes.paddingTablet
                            );
                            return `
                                ${
                                    marginTablet?.top
                                        ? `margin-top: ${marginTablet.top}!important;`
                                        : ""
                                }
                                ${
                                    marginTablet?.right
                                        ? `margin-right: ${marginTablet.right}!important;`
                                        : ""
                                }
                                ${
                                    marginTablet?.bottom
                                        ? `margin-bottom: ${marginTablet.bottom}!important;`
                                        : ""
                                }
                                ${
                                    marginTablet?.left
                                        ? `margin-left: ${marginTablet.left}!important;`
                                        : ""
                                }
                                ${
                                    marginTablet?.blockStart
                                        ? `margin-block-start: ${marginTablet.blockStart}!important;`
                                        : ""
                                }
                                ${
                                    marginTablet?.blockEnd
                                        ? `margin-block-end: ${marginTablet.blockEnd}!important;`
                                        : ""
                                }
                                ${
                                    paddingTablet?.top
                                        ? `padding-top: ${paddingTablet.top}!important;`
                                        : ""
                                }
                                ${
                                    paddingTablet?.right
                                        ? `padding-right: ${paddingTablet.right}!important;`
                                        : ""
                                }
                                ${
                                    paddingTablet?.bottom
                                        ? `padding-bottom: ${paddingTablet.bottom}!important;`
                                        : ""
                                }
                                ${
                                    paddingTablet?.left
                                        ? `padding-left: ${paddingTablet.left}!important;`
                                        : ""
                                }
                            `;
                        })()}
                    }
                }

                @media (max-width: 767px) {
                    #block-${clientId}.has-custom-spacing{
                        /* Mobile styles */
                        ${(() => {
                            const marginMobile = generateSpacingValue(
                                attributes.marginMobile
                            );
                            const paddingMobile = generateSpacingValue(
                                attributes.paddingMobile
                            );
                            return `
                                ${
                                    marginMobile?.top
                                        ? `margin-top: ${marginMobile.top}!important;`
                                        : ""
                                }
                                ${
                                    marginMobile?.right
                                        ? `margin-right: ${marginMobile.right}!important;`
                                        : ""
                                }
                                ${
                                    marginMobile?.bottom
                                        ? `margin-bottom: ${marginMobile.bottom}!important;`
                                        : ""
                                }
                                ${
                                    marginMobile?.left
                                        ? `margin-left: ${marginMobile.left}!important;`
                                        : ""
                                }
                                ${
                                    marginMobile?.blockStart
                                        ? `margin-block-start: ${marginMobile.blockStart}!important;`
                                        : ""
                                }
                                ${
                                    marginMobile?.blockEnd
                                        ? `margin-block-end: ${marginMobile.blockEnd}!important;`
                                        : ""
                                }
                                ${
                                    paddingMobile?.top
                                        ? `padding-top: ${paddingMobile.top}!important;`
                                        : ""
                                }
                                ${
                                    paddingMobile?.right
                                        ? `padding-right: ${paddingMobile.right}!important;`
                                        : ""
                                }
                                ${
                                    paddingMobile?.bottom
                                        ? `padding-bottom: ${paddingMobile.bottom}!important;`
                                        : ""
                                }
                                ${
                                    paddingMobile?.left
                                        ? `padding-left: ${paddingMobile.left}!important;`
                                        : ""
                                }
                            `;
                        })()}
                    }
                }
            `;

            React.useEffect(() => {
                const currentClassName = attributes.className || "";
                const hasSpacingClass =
                    currentClassName.includes("has-custom-spacing");

                if (attributes.enableCustomSpacing && !hasSpacingClass) {
                    setAttributes({
                        className: currentClassName
                            ? `${currentClassName} has-custom-spacing`
                            : "has-custom-spacing",
                    });
                } else if (!attributes.enableCustomSpacing && hasSpacingClass) {
                    setAttributes({
                        className: currentClassName
                            .replace("has-custom-spacing", "")
                            .trim(),
                    });
                }
            }, [attributes.enableCustomSpacing, props.className]);

            return (
                <>
                    {attributes.enableCustomSpacing && (
                        <style>{editorSpacingStyles}</style>
                    )}
                    <BlockEdit {...props} />
                    <InspectorControls group="styles">
                        <PanelBody title="Spacing" initialOpen={true}>
                            <View style={{ padding: "5px 0 15px 0" }}>
                                <ToggleControl
                                    label="Responsive spacing"
                                    checked={attributes.enableCustomSpacing}
                                    onChange={(value) =>
                                        setAttributes({
                                            enableCustomSpacing: value,
                                        })
                                    }
                                    __nextHasNoMarginBottom={true}
                                />
                            </View>

                            {attributes.enableCustomSpacing && (
                                <View style={{ marginBottom: "15px" }}>
                                    <ResponsiveRange
                                        type="multi"
                                        name="margin"
                                        tabLabel="Margin"
                                        attributes={attributes}
                                        updateValue={updateSpacing}
                                        onReset={resetSpacing}
                                    />
                                    <ResponsiveRange
                                        type="multi"
                                        name="padding"
                                        tabLabel="Padding"
                                        attributes={attributes}
                                        updateValue={updateSpacing}
                                        onReset={resetSpacing}
                                    />
                                </View>
                            )}
                        </PanelBody>
                    </InspectorControls>
                </>
            );
        };
    },
    "withSpacingControls"
);

// Dodaj filtry
addFilter(
    "blocks.registerBlockType",
    "danone/spacing-attributes",
    spacingExtension
);

addFilter(
    "editor.BlockEdit",
    "danone/with-spacing-controls",
    withSpacingControls
);
